import { ComponentFactory, ViewContainerRef } from "@angular/core";
import { PopoverController } from "@ionic/angular";
import { BehaviorSubject, Subscription } from "rxjs";
import { combineLatest } from 'rxjs';
import { BaseSimpleChartConfig, GraphDataConfig } from "../objects/config";
import { ConfigDataService } from "../services/config-data.service";
import { GraphDataService } from "../services/graph-data-service.service";
import { ViewPeriodService } from "../services/view-period.service";
import { GraphResolverBase } from "./GraphResolverBase";
import { DynamicEchartComponent } from "../shared-components/dynamic/dynamic-echart/dynamic-echart.component";
import { EChartsOption } from "echarts";
import { GraphDependency } from "../enum/graph-dependency.enum";
import { SankeyRawItem, SankeyOptions, SankeyNode, SankeyLink, LinkRow, SankeyParams, NodeKeyOverride } from "../objects/sankeyTypes";
import { IFetchData, SankeyRawLinks } from "../objects/fetchObejct";
import { CustomTextTooltipsComponent } from "../pages/home/general/custom-text-tooltips/custom-text-tooltips.component";

interface LinkKey {
    src_id: string; src_label: string; src_depth: number;
    tgt_id: string; tgt_label: string; tgt_depth: number;
}
type AggSankeyRow = LinkKey & { count: number; weighted_count?: number };

/**
 * wrapper to turn IFetchData → SankeyRawItem[]
 * @param fetchDatas IFetchData<SankeyRawLinks[]>[]
 * @returns SankeyRawItem[]
 */
export function sankeyFromFetch(
    fetchDatas: IFetchData<SankeyRawLinks[]>[],
): SankeyRawItem[] {
    return fetchDatas.flatMap(fetchData => {
        const areaTag = fetchData.area ?? '';
        return fetchData.data.map((link: SankeyRawLinks) => ({
            count: link.count,
            previous_node: {
                single_id: link.previous_node.single_id!,
                node: link.previous_node.node!,
                depth: link.previous_node.depth
            },
            current_node: {
                single_id: link.current_node.single_id!,
                node: link.current_node.node!,
                depth: link.current_node.depth
            },
            area: areaTag
        }));
    });
}

/**
 * Create a resolver for final ECharts node "name" (i.e., `${key}@${depth}`),
 * with labelMapping and multiple overrides applied.
 *
 * - labelMapping is applied to the original base key.
 * - If an override matches (by index or key[@depth]), the override wins and
 *   becomes the final base key (labelMapping is NOT re-applied to the override).
 *
 * Return: (idx) => string  // final name for node at index idx
 */
function createNodeNameResolver(
    nodes: SankeyNode[],
    labelMapping: Record<string, string> = {},
    overrides: NodeKeyOverride[] = []
): (idx: number) => string {
    const baseKey = (id: string) => id.split('@')[0];
    const stripDepthSuffix = (s: string) => s.split('@')[0];
    const parseKeyDepth = (s: string): { key: string; depth: number | null } => {
        const [k, d] = s.split('@');
        return { key: k, depth: d !== undefined && d !== '' && !Number.isNaN(+d) ? +d : null };
    };

    // Normalize overrides once
    const normalized = overrides.map(o => {
        const parsed = o.key ? parseKeyDepth(o.key) : null;
        return {
            index: o.index,
            matchKey: parsed ? parsed.key : undefined,
            matchDepth: parsed ? parsed.depth : null,
            newKey: stripDepthSuffix(o.newKey),
        };
    });

    const findOverride = (node: SankeyNode, idx: number) => {
        const original = baseKey(node.id);
        const d = node.depth;
        for (const ov of normalized) {
            const byIndex = ov.index !== undefined && ov.index === idx;
            const byKey = ov.matchKey !== undefined
                && ov.matchKey === original
                && (ov.matchDepth === null || ov.matchDepth === d);
            if (byIndex || byKey) return ov;
        }
        return null;
    };

    // Precompute final names for all nodes (keeps links consistent)
    const names = nodes.map((node, idx) => {
        const original = baseKey(node.id);
        const ov = findOverride(node, idx);
        const finalKey = ov ? ov.newKey : original;
        const mapped = labelMapping[finalKey] ?? finalKey;
        return `${mapped.replace('\n', ' ')}@${node.depth}`;
    });

    return (idx: number) => names[idx];
}

function makeSankeyDataV2(
    raw: SankeyRawItem[],
    {
        area,
        maxDepth,
        depthThreshold = 2,
        cumulativePctCutoff = 0.9,
        minCount,
        doWeight = false,
        showPct = false,
        minPaths = 0
    }: SankeyOptions
): { nodes: SankeyNode[]; links: SankeyLink[] } {

    // 1) normalize & rename, filter out items with undefined nodes
    let df: LinkRow[] = raw
        .filter(item => item.previous_node !== undefined && item.current_node !== undefined)
        .map(item => ({
            count: item.count,
            src_id: item.previous_node.single_id ?? item.previous_node.node,
            src_label: item.previous_node.node,
            src_depth: item.previous_node.depth,
            tgt_id: item.current_node.single_id ?? item.current_node.node,
            tgt_label: item.current_node.node,
            tgt_depth: item.current_node.depth,
            area: item.area
        }));

    // 2) rebase depths so min = 0
    const allDepths = df.flatMap(r => [r.src_depth, r.tgt_depth]);
    const minD = Math.min(...allDepths);
    if (minD !== 0) {
        df = df.map(r => ({
            ...r,
            src_depth: r.src_depth - minD,
            tgt_depth: r.tgt_depth - minD
        }));
    }

    // 3) filter by area if given
    if (area != null) {
        const allowed = Array.isArray(area) ? area : [area];
        df = df.filter(r => allowed.includes(r.area));
    }

    // 4) collapse anything beyond maxDepth → exit@maxDepth+1
    if (maxDepth != null) {
        df = df.map(r => {
            if (r.tgt_depth > maxDepth) {
                return {
                    ...r,
                    tgt_id: `exit@${maxDepth + 1}`,
                    tgt_label: 'exit',
                    tgt_depth: maxDepth + 1
                };
            }
            return r;
        });
    }

    // 5) drop by minCount
    if (minCount != null) {
        df = df.filter(r => r.count >= minCount);
    }

    // 6) inject exit@d rows for drop-offs
    const incoming = new Map<string, number>();
    const outgoing = new Map<string, number>();
    df.forEach(r => {
        const ik = `${r.tgt_id}|${r.tgt_depth}`, ok = `${r.src_id}|${r.src_depth}`;
        incoming.set(ik, (incoming.get(ik) || 0) + r.count);
        outgoing.set(ok, (outgoing.get(ok) || 0) + r.count);
    });

    const exitRows: AggSankeyRow[] = [];
    for (const [nodeKey, inCount] of incoming) {
        const [nid, ndStr] = nodeKey.split('|');
        const nd = +ndStr;
        const outCount = outgoing.get(nodeKey) || 0;
        const diff = inCount - outCount;
        if (
            diff > 0 &&
            !nid.startsWith('exit@') && !nid.startsWith('other@') &&
            (maxDepth == null || nd <= maxDepth)
        ) {
            exitRows.push({
                src_id: nid, src_label: nid, src_depth: nd,
                tgt_id: `exit@${nd + 1}`, tgt_label: 'exit', tgt_depth: nd + 1,
                count: diff
            });
        }
    }
    if (exitRows.length) df.push(...exitRows);

    // 7) cumulative-% grouping with bidirectional rewrite
    if (maxDepth != null) {
        for (let d = depthThreshold + 1; d <= maxDepth; d++) {
            const sub = df.filter(r =>
                r.tgt_depth === d &&
                !r.tgt_id.startsWith('exit@') &&
                !r.tgt_id.startsWith('other@')
            );
            if (sub.length === 0 || sub.length <= minPaths) continue;

            // sum & sort
            const totals: Record<string, number> = {};
            sub.forEach(r => { totals[r.tgt_id] = (totals[r.tgt_id] || 0) + r.count; });
            const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
            const sumAll = sorted.reduce((sum, [, c]) => sum + c, 0);

            // keep by cumulative pct
            let cum = 0;
            const keep = new Set<string>();
            for (const [id, c] of sorted) {
                cum += c;
                if (cum / sumAll <= cumulativePctCutoff) keep.add(id);
            }

            const small = sorted.map(([id]) => id).filter(id => !keep.has(id));
            if (!small.length) continue;
            const otherId = `other@${d}`;

            df = df.map(r =>
                r.tgt_depth === d && small.includes(r.tgt_id)
                    ? { ...r, tgt_id: otherId, tgt_label: 'other' }
                    : r
            );
            df = df.map(r =>
                r.src_depth === d && small.includes(r.src_id)
                    ? { ...r, src_id: otherId, src_label: 'other' }
                    : r
            );

            // collapse duplicates
            const groupedMap = new Map<string, LinkRow>();
            df.forEach(r => {
                const key = [r.src_id, r.src_label, r.src_depth, r.tgt_id, r.tgt_label, r.tgt_depth].join('|');
                if (groupedMap.has(key)) groupedMap.get(key)!.count += r.count;
                else groupedMap.set(key, { ...r });
            });
            df = Array.from(groupedMap.values());
        }
    }

    // 8) optional weighting
    if (doWeight) {
        const total0 = df.filter(r => r.src_depth === 0).reduce((s, r) => s + r.count, 0);
        df = df.map(r => ({
            ...r,
            flow_pct: r.count / total0,
            weighted_count: r.count + (r.count / total0) * total0
        }));
    }

    // 9) DEDUPE ACROSS AREAS
    const dedup = new Map<string, AggSankeyRow>();
    df.forEach(r => {
        const key = [r.src_id, r.src_label, r.src_depth, r.tgt_id, r.tgt_label, r.tgt_depth].join('|');
        const prev = dedup.get(key);
        if (prev) prev.count += r.count;
        else dedup.set(key, { ...r });
    });
    df = Array.from(dedup.values());

    // 10) prune unreachable
    const roots = new Set(df.filter(r => r.src_depth === 0).map(r => r.src_id));
    const reachable = new Set(roots);
    let added: boolean;
    do {
        added = false;
        df.forEach(r => {
            if (reachable.has(r.src_id) && !reachable.has(r.tgt_id)) {
                reachable.add(r.tgt_id);
                added = true;
            }
        });
    } while (added);
    df = df.filter(r => reachable.has(r.src_id));

    // 11) build & sort nodes and links
    const nodeMap = new Map<string, { label: string; depth: number; flow: number }>();
    df.forEach(r => {
        const add = (id: string, label: string, depth: number, cnt: number) => {
            const p = nodeMap.get(id) || { label, depth, flow: 0 };
            p.flow += cnt;
            nodeMap.set(id, p);
        };
        add(r.src_id, r.src_label, r.src_depth, r.count);
        add(r.tgt_id, r.tgt_label, r.tgt_depth, r.count);
    });

    const nodes: SankeyNode[] = Array.from(nodeMap.entries())
        .map(([id, m]) => ({ id, label: m.label, depth: m.depth, index: 0 }))
        .sort((a, b) => {
            if (a.depth !== b.depth) return a.depth - b.depth;
            const rank = (id: string) => id.startsWith('exit@') ? 2 : id.startsWith('other@') ? 1 : 0;
            const ra = rank(a.id), rb = rank(b.id);
            if (ra !== rb) return ra - rb;
            if (ra === 0) return (nodeMap.get(b.id)!.flow - nodeMap.get(a.id)!.flow);
            return 0;
        })
        .map((n, i) => ({ ...n, index: i }));

    const entryTotal = df.filter(r => r.src_depth === 0).reduce((s, r) => s + r.count, 0);
    const id2idx = new Map(nodes.map(n => [n.id, n.index]));
    const links: SankeyLink[] = df.map(r => ({
        source: id2idx.get(r.src_id)!,
        target: id2idx.get(r.tgt_id)!,
        value: showPct ? (r.count / entryTotal * 100) : r.count
    }));

    return { nodes, links };
}

// Generate the Sankey chart option
function getSankeyOption(
    nodes: SankeyNode[],
    links: SankeyLink[],
    showPct: boolean = false,
    isShowLabel: boolean = true,
    labelMapping: { [key: string]: string } = {},
    nodeKeyOverrides: NodeKeyOverride[] = []
): EChartsOption {
    const depthColors = [
        '#D1F9FF', '#A8ECFF', '#7FD5F8', '#4FBFF4', '#3899D9',
        '#2E7EB9', '#24669A', '#1A4D7A', '#2F5B72', '#455A64',
    ];

    const nameOf = createNodeNameResolver(nodes, labelMapping, nodeKeyOverrides);

    // Build links using resolved names (keeps endpoints in sync)
    const linkList = links.map((link) => ({
        source: nameOf(link.source),
        target: nameOf(link.target),
        value: link.value,
    }));

    // Build nodes with styles using resolved names
    const nodeList = nodes.map((node, idx) => {
        let color: string;
        if (node.id.startsWith('exit')) color = '#E57373';
        else if (node.id.startsWith('other@')) color = '#FFB300';
        else color = depthColors[node.depth % depthColors.length];

        return {
            name: nameOf(idx),
            itemStyle: { color },
            label: { color: '#FFFFFF' },
            depth: node.depth
        };
    });

    const option: EChartsOption = {
        title: {
            show: nodeList.length === 0 && linkList.length === 0,
            text: 'No Data Available',
            left: 'center',
            top: 'middle',
            textStyle: { fontSize: 18, color: '#fff' },
            backgroundColor: 'rgba(33, 33, 33, 0.8)',
        },
        tooltip: {
            trigger: 'item',
            textStyle: { fontSize: 12 },
            formatter: (params: any) => {
                if (params.dataType === 'node') {
                    return `${params.name}<br/>${showPct ? (params?.value as number).toFixed(1) + '%' : params.value
                        }`;
                } else if (params.dataType === 'edge') {
                    return `${params.data.source} → ${params.data.target}<br/>${showPct ? (params?.value as number).toFixed(1) + '%' : params.value
                        }`;
                }
                return '';
            }
        },
        series: [
            {
                type: 'sankey',
                data: nodeList,
                links: linkList,
                nodeGap: 20,
                orient: 'horizontal',
                layoutIterations: 0,
                right: '5%',
                draggable: false,
                lineStyle: { opacity: 0.5, curveness: 0.5, color: 'gradient' },
                label: {
                    show: isShowLabel,
                    position: 'right',
                    distance: 5,
                    fontSize: 10,
                    fontWeight: 'bold',
                    formatter: (p) => {
                        if (p.value == null) return `${p.name}`;
                        return showPct ? `${p.name}\n${(+p.value).toFixed(1)}%`
                            : `${p.name}\n${p.value}`;
                    },
                },
                emphasis: { focus: 'trajectory' },
            }
        ],
        media: [
            {
                query: { maxWidth: 600 },
                option: {
                    tooltip: { textStyle: { fontSize: 10 } },
                    series: [
                        {
                            nodeGap: 8,
                            orient: 'vertical',
                            layoutIterations: 0,
                            lineStyle: { curveness: 0.3 },
                            label: {
                                show: isShowLabel,
                                fontSize: 8,
                                distance: 3,
                                position: 'top',
                                fontWeight: 'bold',
                            },
                            labelLayout: { hideOverlap: true },
                        }
                    ]
                }
            }
        ]
    };

    return option;
}

export class SankeyChartV2Resolver extends GraphResolverBase {
    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: BaseSimpleChartConfig,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {

        const chartTitleKey = additionalInput.title || 'Sankey Chart';
        const datasourceKey = additionalInput.datasourceKey;
        const selector = additionalInput.selector;
        const infoPopover = additionalInput.infoPopover;
        let shouldShowSelector = true;
        let multipleSelector = false;
        let sankeyParams: Partial<SankeyParams> = {
            depthThreshold: 1,
            cumulativePctCutoff: 0.8,
            maxDepth: 6,
            showPct: true,
            minPaths: 2
        };
        if (additionalInput.params) {
            sankeyParams = additionalInput.params as SankeyParams;
        }
        if (additionalInput.settings) {
            const settings = additionalInput.settings;
            shouldShowSelector = settings.isDisplaySelector ?? true;
            multipleSelector = settings.isMultipleSelector ?? false;
        }

        const sankeyBaseOption: EChartsOption = {};
        // Initialize the sankey chart option
        const chartOption = sankeyBaseOption;
        const chartOption$ = new BehaviorSubject<EChartsOption>(chartOption);
        // Initialize the selected options
        const selectOption: { [selectorName: string]: GraphDataConfig & { selected?: boolean } } = selector;
        const selectedOption$ = new BehaviorSubject<{ [selectorName: string]: GraphDataConfig & { selected?: boolean } }>(selectOption);

        const chartLoading$ = new BehaviorSubject<boolean>(false);
        graphDataService.baseGraphData.addDependency([GraphDependency[datasourceKey]]);
        const areaName = Object.values(selector ?? {})
            .flatMap(selector => Array.isArray(selector.args) ? selector.args : [])
            .join(',');
        if (areaName !== "") {
            // Merge unique values with existing selections
            const current = graphDataService.baseGraphData.selectedPinName$.getValue() ?? '';
            const currentList = current ? current.split(',') : [];
            const newList = areaName.split(',');
            const mergedList = Array.from(new Set([...currentList, ...newList])).filter(v => v);
            graphDataService.baseGraphData.selectedPinName$.next(mergedList.join(','));
        }
        viewPeriodService.subscribeSelectedDate(value => {
            chartLoading$.next(true);
        });

        if (datasourceKey === 'BUILDING_ZONE_TRAFFIC_FLOW_UNMERGED_BY_PIN') {
            combineLatest([
                graphDataService.buildingZoneTrafficFlowUnmergedByPinRaw$, selectedOption$, graphDataService.baseGraphData.selectedInteractable$
            ]).subscribe(([sankeyRawItems, selectOption, selectedInteractable]) => {
                if (sankeyRawItems && sankeyRawItems.length > 0) {
                    let areaList: string[] = [];
                    let nodeKeyOverrides: NodeKeyOverride[] = [];
                    if (selectOption && Object.keys(selectOption).length > 0) {
                        const filteredSelectOption = Object.entries(selectOption)
                            .filter(([key, selector]) => selector.selected)
                        areaList = filteredSelectOption
                            .flatMap(([, selectedArgs]) => selectedArgs.args);
                        nodeKeyOverrides = sankeyParams?.nodeKeyOverride?.filter(nodeKeyOverride => filteredSelectOption.find(([selectedKey,]) => selectedKey === nodeKeyOverride.selectorKey))
                    } else {
                        areaList = [selectedInteractable?.name ?? ''];
                        nodeKeyOverrides = sankeyParams?.nodeKeyOverride?.filter(nodeKeyOverride => nodeKeyOverride.selectorKey === selectedInteractable?.name);
                    }
                    sankeyParams.area = areaList;

                    const { nodes, links } = makeSankeyDataV2(sankeyRawItems, sankeyParams)
                    const option = getSankeyOption(nodes, links, true, true, configDataService.DISPLAY_LANGUAGE.SANKEY_NODE_NAME, nodeKeyOverrides);
                    chartOption$.next(option);
                    chartLoading$.next(false);
                } else if (sankeyRawItems && sankeyRawItems.length === 0) {
                    chartOption$.next({
                        title: {
                            show: true,
                            text: 'No Data Available',
                            left: 'center',
                            top: 'middle',
                            textStyle: {
                                fontSize: 18,
                                color: '#fff',
                            },
                            backgroundColor: 'rgba(33, 33, 33, 0.8)',
                        },
                        series: [
                            {
                                type: 'sankey',
                                data: [],
                                links: [],
                            }
                        ],
                    });
                    chartLoading$.next(false);
                }
            });
        }

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicEchartComponent;
        comInstance.title = chartTitleKey;
        comInstance.isShow = true;
        comInstance.chartOption = chartOption;
        comInstance.chartOption$ = chartOption$;
        comInstance.shouldShowSelector = shouldShowSelector;
        comInstance.multipleSelector = multipleSelector;
        comInstance.selectedOption$ = selectedOption$;
        comInstance.selector = selectOption;
        comInstance.chartLoading$ = chartLoading$;
        if (infoPopover !== undefined && infoPopover !== null) {
            comInstance.infoPopover = async (e: any) => {
                const stackBarPopover = await popoverController.create({
                    component: CustomTextTooltipsComponent,
                    componentProps: {
                        toolTipTitle: configDataService.DISPLAY_LANGUAGE[infoPopover.title] || infoPopover.title || chartTitleKey,
                        toolTipDetails: infoPopover.details,
                    },
                    event: e,
                });
                return await stackBarPopover.present();
            };

        }
        return comInstance;
    }

}